import React, { useEffect, useRef, useState } from 'react'
import { Dropdown, Nav, Navbar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'

import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'

import icon from '../../../assets/sure_admin_icon.png'
import { isNavItem, NavItem, NavItemGroup } from '../types'

import { SignInButton } from './auth/SignInButton'
import { InfoMenu } from './InfoMenu'

type NavDropDownProps = {
  navItems: (NavItem | NavItemGroup)[]
  hasAppConfigurationError?: boolean
}

/**
 * HeaderWithNavBar component.
 *
 * This component renders a navigation bar with a brand logo, navigation links, and a sign-in button.
 *
 * @param {NavDropDownProps} props - The props for the component.
 * @returns {JSX.Element} The rendered header with navigation bar component.
 */
const HeaderWithNavBar: React.FC<NavDropDownProps> = ({
  navItems,
  hasAppConfigurationError,
}): JSX.Element => {
  const { t } = useTranslation()

  const [lastClicked, setLastClicked] = useState<string | null>(null)
  const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>({})
  const [navMap, setNavMap] = useState<Map<string, string[]>>(new Map())
  const [selectedNavItem, setSelectedNavItem] = useState<string | null>(null)

  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  const location = useLocation()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isOutside = Object.values(dropdownRefs.current).every(
        (ref) => ref && !ref.contains(event.target as Node),
      )
      if (isOutside) {
        setLastClicked('')
        setOpenGroups({})
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownRefs])

  useEffect(() => {
    const computeNavMap = (
      items: (NavItem | NavItemGroup)[],
      parentIds: string[] = [],
    ) => {
      const map = new Map<string, string[]>()

      const traverse = (
        items: (NavItem | NavItemGroup)[],
        parentIds: string[],
      ) => {
        items.forEach((item) => {
          map.set(item.id, [...parentIds])
          if (!isNavItem(item)) {
            traverse(item.items, [...parentIds, item.id])
          }
        })
      }

      traverse(items, parentIds)
      return map
    }

    const map = computeNavMap(navItems)
    setNavMap(map)
  }, [navItems])

  useEffect(() => {
    const navId = getTopParentNavIdOfCurrentOpenTab(navItems, location.pathname)
    if (navId) {
      setSelectedNavItem(navId)
    }
  }, [navItems, location.pathname])

  const getTopParentNavIdOfCurrentOpenTab = (
    navItems: (NavItem | NavItemGroup)[],
    currentPath: string,
  ): string | null => {
    for (const item of navItems) {
      if (isNavItem(item)) {
        if (item.to === currentPath) {
          return item.id
        }
      } else {
        const parentId = getTopParentNavIdOfCurrentOpenTab(
          item.items,
          currentPath,
        )
        if (parentId) {
          return item.id
        }
      }
    }

    return null
  }

  const toggleGroup = (id: string): void => {
    setOpenGroups((prev) => {
      const newOpenGroups = { ...prev }
      const parentIds = navMap.get(id) || []

      Object.keys(newOpenGroups).forEach((key) => {
        newOpenGroups[key] = false
      })

      // Open the specified group and its parents
      parentIds.forEach((parentId) => {
        newOpenGroups[parentId] = true
      })
      newOpenGroups[id] = !prev[id]

      return newOpenGroups
    })
  }

  const handleNavDropDownGroupClick = (id: string): void => {
    setLastClicked(id)
    toggleGroup(id)
  }

  const handleNavDropDownItemClick = (id: string): void => {
    setLastClicked('')
    toggleGroup('')
  }

  const renderNavDropdownItems = (
    items: (NavItem | NavItemGroup)[],
    level = 0,
  ): JSX.Element => {
    return (
      <Dropdown.Menu style={{ ...dropdownMenuStyle }}>
        {items.map((item) => {
          if (isNavItem(item)) {
            return (
              <Dropdown.Item
                as="div"
                key={item.to}
                style={{
                  ...dropdownItemStyle,
                  ...(lastClicked === item.id ? lastClickedStyle : {}),
                }}
                onClick={() => handleNavDropDownItemClick(item.id)}
              >
                <NavLink
                  to={item.to}
                  style={{
                    color: 'inherit',
                    textDecoration: 'none',
                    width: '100%',
                  }}
                >
                  {item.label}
                </NavLink>
              </Dropdown.Item>
            )
          } else {
            const isOpen = openGroups[item.id] || false
            return (
              <>
                <Dropdown.Item
                  as="div"
                  key={item.id}
                  style={{
                    ...dropdownItemStyle,
                    ...(lastClicked === item.id ? lastClickedStyle : {}),
                  }}
                  onClick={() => handleNavDropDownGroupClick(item.id)}
                >
                  <span style={{ marginRight: '20px' }}>{item.name}</span>
                  <span style={arrowIconStyle}>
                    {isOpen ? <NavigateBeforeIcon /> : <NavigateNextIcon />}
                  </span>
                </Dropdown.Item>
                {isOpen && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: '100%',
                      marginLeft: '5px',
                      marginTop: '-11px',
                    }}
                  >
                    {renderNavDropdownItems(item.items, level + 1)}
                  </span>
                )}
              </>
            )
          }
        })}
      </Dropdown.Menu>
    )
  }

  return (
    <Navbar variant="dark" style={{ background: '#0096d6' }}>
      <Nav className="mr-auto" defaultActiveKey="/keys">
        <Navbar.Brand href="/keys" style={{ fontWeight: 500 }}>
          <img
            alt="KMS Logo"
            src={icon}
            height="35"
            className="d-inline-block align-top"
            style={{ marginLeft: '10px' }}
          />{' '}
          {t('kms_admin_portal')}
        </Navbar.Brand>
        {navItems.map((item) => {
          if (isNavItem(item)) {
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className="nav-link"
                style={{
                  color: selectedNavItem === item.id ? 'white' : '#E2E3FB',
                }}
              >
                {item.label}
              </NavLink>
            )
          } else {
            return (
              <Dropdown
                key={item.id}
                show={openGroups[item.id] || false}
                ref={(el) => (dropdownRefs.current[item.id] = el)}
              >
                <Dropdown.Toggle
                  as={({ children, onClick }: any) => (
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        toggleGroup(item.id)
                        setLastClicked(item.id)
                        onClick(e)
                      }}
                      style={{
                        color:
                          selectedNavItem === item.id ? 'white' : '#E2E3FB',
                      }}
                      className="nav-link"
                    >
                      {children}
                    </button>
                  )}
                  id="dropdown-custom-components"
                >
                  <span>{item.name}</span>
                </Dropdown.Toggle>
                {renderNavDropdownItems(item.items)}
              </Dropdown>
            )
          }
        })}
      </Nav>
      <Navbar.Toggle />
      {!hasAppConfigurationError && (
        <Navbar.Collapse className="justify-content-end">
          <SignInButton />
        </Navbar.Collapse>
      )}
      <InfoMenu />
    </Navbar>
  )
}

export default HeaderWithNavBar

const dropdownMenuStyle: React.CSSProperties = {
  marginTop: '10px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
  borderRadius: '15px',
  border: 'none',
}

const dropdownItemStyle: React.CSSProperties = {
  fontSize: 'inherit',
  width: 'calc(100% - 20px)',
  height: '100%',
  padding: '8px 12px',
  margin: '2px 10px',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  borderRadius: '10px',
}

const lastClickedStyle: React.CSSProperties = {
  backgroundColor: '#A3A3A333',
}

const arrowIconStyle: React.CSSProperties = {
  marginLeft: 'auto',
}
